import {
  builder,
  BuilderConfig,
  Device,
  PageBuilderResponse,
  PublicPageResponse,
} from '@tempi/core-renderer';
import { SSR_COMPONENT_TAGS } from '@tempi/ecom-elements/dist/constants/dynamic-block';
import { getSlotData } from '@tempi/ecom-elements/dist/utils/getSlotData';
import { getEnv, getLocalDomain, isServerless } from 'helpers';
import { getContentById, removeComponentByKey } from './helper';
import { ElementInterface, LayoutConfig, Content, SSRInput } from './types';

type Builder = typeof builder;

type PbContent = PublicPageResponse & {
  layoutConfig?: LayoutConfig;
};

export class PbService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builder: any;
  domain: string;
  appliedDomain?: string;
  configs: BuilderConfig;
  constructor(host: string, appliedDomain: string, isEpicEcom480: boolean) {
    const env = (getEnv('LANDING_BUILDER_ENV') ||
      'dev') as BuilderConfig['env'];
    this.domain = isEpicEcom480 ? host : getLocalDomain(host, env);

    const epicEcom480Phase2 = getEnv('EPIC_ECOM_480_PHASE_2') === 'enabled';
    if (epicEcom480Phase2) {
      this.appliedDomain = getLocalDomain(appliedDomain, env);
    }

    this.configs = {
      env: isServerless() ? env : 'internal',
    };
  }

  async getBuilder(): Promise<Builder> {
    if (!this.builder) {
      this.builder = await import('@tempi/core-renderer')
        .then((f) => f.builder)
        .then((builder) => {
          builder.init(this.domain, { ...this.configs });
          return builder;
        });
    }
    return this.builder;
  }

  private async getPublicPageV2(
    page = '/',
    device: Device,
    terminalId?: number,
  ) {
    const builder = await this.getBuilder();
    return builder.getPublicPageV2({
      slug: page,
      device,
      appliedDomain: this.appliedDomain,
      terminalId,
    });
  }

  async getSSRInputs(content: Content, device: Device) {
    const builder: Builder = await this.getBuilder();
    return builder.getSSRInputs<ElementInterface, SSRInput>(
      // I'm not so sure about this assertion :(
      content as PageBuilderResponse<ElementInterface>,
      {
        device,
        ssrComponentTags: SSR_COMPONENT_TAGS,
        getSlotDataFunc: getSlotData,
      },
    );
  }

  /**
   * only return content config, exclude header, footer
   * @returns
   * @param page
   * @param device
   */
  async getContentPage(
    page: string,
    device: Device,
    terminalId?: number,
  ): Promise<PbContent> {
    const res = await this.getPublicPageV2(page, device, terminalId);
    let content: Content = { ...res.content };
    content = removeComponentByKey(
      'FOOTER',
      removeComponentByKey('HEADER', content),
    );
    //  remove key Header and footer in root content
    content['ROOT'].children = content['ROOT'].children.filter(
      (id: string) => id !== 'HEADER' && id !== 'FOOTER',
    );
    return { ...res, content };
  }

  /**
   * only return layout config
   * include header, footer, root and its children
   */
  async getLayoutPage(device: Device): Promise<PbContent> {
    const { content, ...res } = await this.getPublicPageV2('404', device);
    return {
      ...res,
      layoutConfig: {
        header: getContentById('HEADER', content),
        footer: getContentById('FOOTER', content),
      },
    };
  }
}
